import { z } from 'zod';

/*
 * The field builders every print schema is assembled from. Each carries a
 * `.default()`, because a stored document is re-parsed on the way *out* of
 * Postgres and a field added later must not make every existing template
 * unreadable.
 *
 * Not re-exported from `#shared/types`: `mm` and `px` there are the geometry
 * conversions in print-geometry.ts, which these would shadow.
 */

/**
 * A point size.
 *
 * Four is the smallest thing anybody can read on paper and 72 is an inch; the
 * bounds exist so a stored document cannot render a report as one enormous
 * character or as an invisible one.
 */
export const pt = (fallback: number) => z.coerce.number().min(4).max(72).default(fallback);

/**
 * A single line of template text.
 *
 * Trimmed and bounded. Every one has a `.default()` because a stored document
 * is re-parsed on the way *out* of Postgres — a field added later must not make
 * every existing template unreadable.
 */
export const line = (fallback = '') => z.string().trim().max(200).default(fallback);

/**
 * A six-digit hex colour.
 *
 * Deliberately not a CSS colour: the value is handed to a PDF, where `oklch()`,
 * `currentColor` and named colours have no meaning. The builder's colour inputs
 * are `<input type="color">`, which emits exactly this form.
 */
export const hexColor = (fallback: string) =>
  z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a six-digit hex colour, e.g. #2d358a')
    .default(fallback);

/** A length in millimetres. */
export const mm = (fallback: number, max = 200) =>
  z.coerce.number().min(0).max(max).default(fallback);

/** A length in CSS pixels, 0–800 — the blank space left for pre-printed stationery. */
export const px = (fallback: number) => z.coerce.number().int().min(0).max(800).default(fallback);
